
import jsPDF from 'jspdf';
import { captureSection } from './screenshotCapture';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { FilterSummary } from '../FilterSummary';

const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const MARGIN = 10;
const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2);
const CONTENT_HEIGHT = A4_HEIGHT - (MARGIN * 2);

function canvasToDataUrl(canvas) {
  return canvas.toDataURL('image/png', 1.0);
}

function addCanvasAcrossPages(pdf, canvas, startY = MARGIN) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - (MARGIN * 2);
  const availableHeightFirstPage = pageHeight - startY - MARGIN;
  const availableHeightFullPage = pageHeight - (MARGIN * 2);

  const pxPerMm = canvas.width / contentWidth;


  let consumedPx = 0;
  let currentY = startY;
  let isFirstPageSlice = true;
  let pageCount = 0;

  while (consumedPx < canvas.height) {
    const sliceHeightMm = isFirstPageSlice ? availableHeightFirstPage : availableHeightFullPage;
    const sliceHeightPx = Math.floor(sliceHeightMm * pxPerMm);
    const remainingPx = canvas.height - consumedPx;
    const actualSlicePx = Math.min(sliceHeightPx, remainingPx);


    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = actualSlicePx;
    const sliceCtx = sliceCanvas.getContext('2d');

    if (sliceCtx) {
      sliceCtx.fillStyle = '#ffffff';
      sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);

      sliceCtx.drawImage(
        canvas,
        0,
        consumedPx,
        canvas.width,
        actualSlicePx,
        0,
        0,
        sliceCanvas.width,
        sliceCanvas.height
      );
    }

    const renderHeightMm = actualSlicePx / pxPerMm;

    pdf.addImage(
      canvasToDataUrl(sliceCanvas),
      'PNG',
      MARGIN,
      currentY,
      contentWidth,
      renderHeightMm,
      undefined,
      'SLOW' // Maximum quality, no compression
    );

    consumedPx += actualSlicePx;
    pageCount++;

    if (consumedPx < canvas.height) {
      pdf.addPage();
      currentY = MARGIN;
      isFirstPageSlice = false;
    }
  }

}

function calculateScale(canvasWidth, canvasHeight) {
  const widthScale = CONTENT_WIDTH / (canvasWidth * 0.264583); // px to mm
  const heightScale = CONTENT_HEIGHT / (canvasHeight * 0.264583);
  const scale = Math.min(widthScale, heightScale, 1);

  const finalScale = Math.max(scale, 0.5);

  const width = (canvasWidth * 0.264583) * finalScale;
  const height = (canvasHeight * 0.264583) * finalScale;

  return { scale: finalScale, width, height };
}

async function renderFilterSummary(filterData) {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '1600px';
  container.style.maxWidth = '1600px';
  container.style.backgroundColor = '#ffffff';
  container.style.padding = '20px';
  document.body.appendChild(container);

  const FilterSummaryElement = React.createElement(FilterSummary, filterData);
  const root = createRoot(container);

  return new Promise((resolve) => {
    root.render(FilterSummaryElement);
    setTimeout(() => {
      const summaryElement = container.querySelector('.ci-filter-summary');
      if (summaryElement) {
        summaryElement.style.minWidth = '1560px';
        summaryElement.style.maxWidth = '1560px';
      }

      const labels = container.querySelectorAll('.ci-filter-summary-label');
      labels.forEach(label => {
        label.style.color = '#000000';
        label.style.fontWeight = '600';
      });

      const values = container.querySelectorAll('.ci-filter-summary-value');
      values.forEach(value => {
        value.style.color = '#000000';
        value.style.wordWrap = 'break-word';
        value.style.overflowWrap = 'break-word';
        value.style.whiteSpace = 'normal';
      });

      const header = container.querySelector('.ci-filter-summary-header');
      if (header) {
        header.style.color = '#7c3aed';
      }

      const note = container.querySelector('.ci-filter-summary-note');
      if (note) {
        note.style.color = '#666666';
      }

      resolve({ container, root });
    }, 150);
  });
}

function cleanupElement(renderedElement) {
  if (!renderedElement || !renderedElement.container) {
    return;
  }

  const { container, root } = renderedElement;
  if (root) {
    root.unmount();
  }
  if (container.parentNode) {
    container.parentNode.removeChild(container);
  }
}

export async function exportSectionScreenshotPDF(sectionId, filename, prepareCallback, filterData) {
  let filterElement = null;

  try {

    if (prepareCallback) {
      await prepareCallback();
    }

    let filterCanvas = null;
    if (filterData) {
      try {
        filterElement = await renderFilterSummary(filterData);
        const { captureElement } = await import('./screenshotCapture');
        filterCanvas = await captureElement(filterElement.container);
      } catch (error) {
        console.warn('📸 [Screenshot PDF] Filter summary capture failed, continuing without it:', error);
        filterCanvas = null;
      }
    }

    const sectionCanvas = await captureSection(sectionId);

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    let yOffset = MARGIN;

    if (filterCanvas) {
      const filterImgData = canvasToDataUrl(filterCanvas);
      const filterScale = calculateScale(filterCanvas.width, filterCanvas.height);

      pdf.addImage(
        filterImgData,
        'PNG',
        MARGIN,
        yOffset,
        filterScale.width,
        filterScale.height,
        undefined,
        'SLOW' // Maximum quality
      );

      yOffset += filterScale.height + 5;
    }

    addCanvasAcrossPages(pdf, sectionCanvas, yOffset);

    pdf.save(filename);
  } catch (error) {
    console.error('❌ [Screenshot PDF] Export failed:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      sectionId,
      filename,
    });
    throw new Error(`Screenshot capture failed: ${error.message || 'Unknown error'}`);
  } finally {
    if (filterElement) {
      cleanupElement(filterElement);
    }
  }
}

async function openModal(sectionId) {
  const baseId = sectionId.replace('-modal', '');


  const classInsightsContext = window.__classInsightsContext__;
  if (!classInsightsContext) {
    console.error(`❌ [Screenshot PDF] classInsightsContext not available`);
    return false;
  }

  if (classInsightsContext?.expandPanel) {
    classInsightsContext.expandPanel(baseId, true);
  } else {
    const expandButton = document.querySelector(
      `[data-section="${baseId}"] .ci-icon-button-expand, [data-section="${baseId}"] .ci-panel-expand-btn, [data-section="${baseId}"] .ci-expand-btn`
    );

    if (expandButton) {
      expandButton.click();
    } else {
      console.error(`❌ [Screenshot PDF] Could not find expand button for ${baseId}`);
      return false;
    }
  }

  const maxRetries = 35;
  const retryDelay = 200;
  for (let i = 0; i < maxRetries; i++) {
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    const modalElement = document.querySelector(`[data-section="${sectionId}"]`);
    if (!modalElement) continue;

    const hasLoader = modalElement.querySelector('.ci-panel-loading, .small-loader-wrapper');
    if (hasLoader) continue;

    const hasRenderableContent = modalElement.querySelector(
      '.ci-modal-list, .ci-modal-table, .ci-sc-grid, table, .ci-empty-state'
    );
    if (!hasRenderableContent) continue;

    await new Promise((resolve) => setTimeout(resolve, 300));
    return true;
  }

  console.warn(`⚠️ [Screenshot PDF] Could not open modal for ${baseId}`);
  return false;
}

async function closeModal() {
  const closeButton = document.querySelector('.ci-modal-close, .ci-close-btn, .ci-modal-close-btn');
  if (closeButton) {
    closeButton.click();
    await new Promise(resolve => setTimeout(resolve, 300));
  }
}

function enableSilentModalCapture() {
  const style = document.createElement('style');
  style.setAttribute('data-ci-export-silent-modal', 'true');
  style.textContent = `
    .ci-modal-overlay {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .ci-modal:not([data-ci-export-clone]),
    .ci-modal-wide:not([data-ci-export-clone]) {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
  return () => {
    if (style.parentNode) {
      style.parentNode.removeChild(style);
    }
  };
}

export async function exportFullDashboardScreenshotPDF(filename, prepareCallback, filterData) {
  let filterElement = null;
  let disableSilentModalCapture = null;

  try {

    if (prepareCallback) {
      await prepareCallback();
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    let isFirstPage = true;

    if (filterData) {
      try {
        filterElement = await renderFilterSummary(filterData);
        const { captureElement } = await import('./screenshotCapture');
        const filterCanvas = await captureElement(filterElement.container);

        if (!isFirstPage) pdf.addPage();
        addCanvasAcrossPages(pdf, filterCanvas, MARGIN);
        isFirstPage = false;

      } catch (error) {
        console.warn('📸 [Screenshot PDF] Filter summary failed:', error);
      }
    }

    disableSilentModalCapture = enableSilentModalCapture();

    const sectionsToCapture = [
      { id: 'summary', name: 'Summary & KPI Cards', isModal: false },
      { id: 'comment-banks', name: 'Comment Banks (All Data)', isModal: true },
      { id: 'success-criteria', name: 'Success Criteria (All Data)', isModal: true },
      { id: 'rubrics', name: 'Rubrics (All Data)', isModal: true },
      { id: 'short-answer', name: 'Short Answer Responses (All Data)', isModal: true },
    ];

    const { captureSection } = await import('./screenshotCapture');
    const missingSections = [];

    for (const { id, name, isModal } of sectionsToCapture) {
      try {

        let sectionId = id;
        let modalOpened = false;

        if (isModal) {
          modalOpened = await openModal(`${id}-modal`);
          if (modalOpened) {
            sectionId = `${id}-modal`;
          } else {
            console.warn(`📸 [Screenshot PDF] Failed to open ${name}`);
            missingSections.push(name);
            continue;
          }
        }

        const sectionElement = document.querySelector(`[data-section="${sectionId}"]`);
        if (!sectionElement) {
          console.warn(`📸 [Screenshot PDF] Section ${sectionId} not found, skipping`);
          if (modalOpened) await closeModal();
          missingSections.push(name);
          continue;
        }

        const sectionCanvas = await captureSection(sectionId);


        if (modalOpened) {
          await closeModal();
        }

        if (!isFirstPage) {
          pdf.addPage();
        }
        isFirstPage = false;

        addCanvasAcrossPages(pdf, sectionCanvas, MARGIN);


        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        console.warn(`📸 [Screenshot PDF] Failed to capture ${name}:`, error);
        await closeModal();
        missingSections.push(name);
      }
    }

    if (isFirstPage) {
      throw new Error('No report sections were available to export.');
    }

    if (missingSections.length > 0) {
      console.warn(
        `PDF export skipped unavailable sections: ${missingSections.join(', ')}`
      );
    }

    pdf.save(filename);

  } catch (error) {
    console.error('❌ [Screenshot PDF] Full dashboard export failed:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      filename,
    });
    throw new Error(`Screenshot capture failed: ${error.message || 'Unknown error'}`);
  } finally {
    if (filterElement) {
      cleanupElement(filterElement);
    }
    if (disableSilentModalCapture) {
      disableSilentModalCapture();
    }
    await closeModal();
  }
}
